"use client";
import { Suspense, use, useEffect, useMemo } from "react";
import { useSearchParams } from "next/navigation";
import { MessageSquare } from "lucide-react";
import useProductReview from "@/app/lib/features/productReview/productReview.hook";
import type {
  ProductReviewData,
  ReviewFilters,
  ReviewsListData,
} from "@/app/lib/features/productReview/productReview.types";
import ReviewProductCard from "@/app/components/common/product-review/ReviewProductCard";
import ReviewSellerInfo from "@/app/components/common/product-review/ReviewSellerInfo";
import FilterSection from "@/app/components/common/product-review/FilterSection";
import ReviewHeader from "@/app/components/common/product-review/ReviewHeader";
import SearchAndSort from "@/app/components/common/product-review/SearchAndSort";
import SizeRecommendation from "@/app/components/common/product-review/SizeRecommendation";
import ReviewSummary from "@/app/components/common/product-review/ReviewSummary";
import PhotoReviews from "@/app/components/common/product-review/PhotoReviews";
import ReviewCard from "@/app/components/common/product-review/ReviewCard";
import FilterSidebar from "@/app/components/common/product-review/FilterSidebar";
import InfiniteScrollTrigger from "@/app/components/common/InfiniteScrollTrigger";
import EmptyState from "@/app/components/elements/EmptyState";
import ReviewCardSkeleton from "@/app/components/elements/skeletons/ReviewCardSkeleton";
import ProductReviewPageSkeleton from "@/app/components/elements/skeletons/ProductReviewPageSkeleton";

/** URL filtrelerini Redux'a senkronlayan izole bileşen.
 *  useSearchParams statik prerender'da en yakın Suspense sınırını CSR'a düşürdüğü
 *  için ayrı bir <Suspense fallback={null}> içine alındı: yalnızca bu boş bileşen
 *  client-render olur, sayfanın geri kalanı ISR HTML'ine girer. */
const ReviewFilterUrlSync = ({
  ready,
  onSync,
}: {
  ready: boolean;
  onSync: (filters: ReviewFilters) => void;
}) => {
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!ready) return;

    const rating = searchParams.getAll("rating").map(Number).filter(Boolean);
    const topic = searchParams.getAll("topic").filter(Boolean);
    const search = searchParams.get("search") || undefined;
    const sort = searchParams.get("sort") || undefined;
    const sellerIdParam = searchParams.get("seller_id");
    const seller_id = sellerIdParam ? parseInt(sellerIdParam) : undefined;

    onSync({
      rating: rating.length > 0 ? rating : undefined,
      topic: topic.length > 0 ? topic : undefined,
      search,
      sort,
      seller_id,
    });
  }, [searchParams, ready, onSync]);

  return null;
};

interface ProductReviewClientProps {
  slug: string;
  initialDataPromise: Promise<ProductReviewData | null>;
  initialListPromise: Promise<ReviewsListData | null>;
}

const ProductReviewClient = ({
  slug,
  initialDataPromise,
  initialListPromise,
}: ProductReviewClientProps) => {
  const initialData = use(initialDataPromise);
  const initialList = use(initialListPromise);

  const {
    productCardData: reduxProductCard,
    sellerInfoData: reduxSellerInfo,
    filtersData: reduxFilters,
    ratingStatsData: reduxRatingStats,
    searchBarData: reduxSearchBar,
    infoBoxData: reduxInfoBox,
    aiSummaryData: reduxAiSummary,
    reviewsList: reduxReviewsList,
    isLoading,
    isError,
    isReviewsListLoading,
    isReviewsListSuccess,
    hasMore,
    currentProductSlug,
    appliedFilters,
    status,
    fetchProductReviews,
    fetchProductReviewsList,
    loadMoreReviews,
    updateCurrentSlug,
    seedInitialReview,
    syncFromUrl,
  } = useProductReview();

  // SSR fallback: sunucuda Redux boş (seed effect'i yalnızca client'ta çalışır),
  // bu yüzden ISR HTML'i doğrudan initialData'dan render edilir. Client'ta Redux
  // dolunca otomatik devralır — hydration'da iki taraf da aynı veriyi gösterir.
  const ssrSections = useMemo(() => {
    const pick = <T,>(type: string) =>
      initialData?.sections.find((s) => s.type === type)?.data as T | undefined;
    return {
      productCard: pick<typeof reduxProductCard>("product_summary"),
      sellerInfo: pick<typeof reduxSellerInfo>("seller_info"),
      filters: pick<typeof reduxFilters>("filters"),
      ratingStats: pick<typeof reduxRatingStats>("rating_stats"),
      searchBar: pick<typeof reduxSearchBar>("search_bar"),
      infoBox: pick<typeof reduxInfoBox>("info_box"),
      aiSummary: pick<typeof reduxAiSummary>("ai_summary"),
    };
  }, [initialData]);

  // Yorum listesi: Redux doluysa o, değilse SSR listesi (ISR HTML'inin asıl
  // SEO içeriği bu — yorum metinleri).
  const reviewsList =
    reduxReviewsList.length > 0 ? reduxReviewsList : initialList?.reviews ?? [];

  const productCardData = reduxProductCard ?? ssrSections.productCard;
  const sellerInfoData = reduxSellerInfo ?? ssrSections.sellerInfo;
  const filtersData = reduxFilters ?? ssrSections.filters;
  const ratingStatsData = reduxRatingStats ?? ssrSections.ratingStats;
  const searchBarData = reduxSearchBar ?? ssrSections.searchBar;
  const infoBoxData = reduxInfoBox ?? ssrSections.infoBox;
  const aiSummaryData = reduxAiSummary ?? ssrSections.aiSummary;

  useEffect(() => {
    if (slug !== currentProductSlug) {
      updateCurrentSlug(slug);
    }
  }, [slug, currentProductSlug, updateCurrentSlug]);

  // SSR/ISR verisini Redux'a yükle. Bu effect fetch effect'inden ÖNCE tanımlı:
  // seed status'u "success" yaptığı için aşağıdaki "status === idle" fetch'i
  // ikinci render'da tetiklenmez → çift istek olmaz. initialData null ise
  // (API hatası) seed atlanır ve normal client fetch devreye girer.
  useEffect(() => {
    if (!initialData && !initialList) return;
    seedInitialReview({ slug, data: initialData, reviewsList: initialList });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!currentProductSlug || status !== "idle") return;

    fetchProductReviews(currentProductSlug);
    fetchProductReviewsList(currentProductSlug, {
      ...appliedFilters,
      page: 1,
      per_page: 15,
    });
  }, [
    currentProductSlug,
    status,
    appliedFilters,
    fetchProductReviews,
    fetchProductReviewsList,
  ]);

  // Her dalda mount kalmalı: aksi halde skeleton/hata ekranındayken URL filtreleri
  // Redux'a senkronlanmaz ve veri gelince bir tur gecikmeli uygulanır.
  const urlSync = (
    <Suspense fallback={null}>
      <ReviewFilterUrlSync ready={!!currentProductSlug} onSync={syncFromUrl} />
    </Suspense>
  );

  if (isLoading) {
    return (
      <>
        {urlSync}
        <ProductReviewPageSkeleton />
      </>
    );
  }

  if (isError) {
    return (
      <div className="py-6 container-xs md:py-8">
        {urlSync}
        <EmptyState
          icon={MessageSquare}
          title="Bir Hata Oluştu"
          description="Değerlendirmeler yüklenirken bir hata oluştu. Lütfen tekrar deneyin."
          actions={[
            {
              // window.location render sırasında okunamaz — sayfa artık SSR ediliyor.
              label: "Tekrar Dene",
              href: `/urun-degerlendirmesi/${slug}`,
              variant: "default",
            },
          ]}
        />
      </div>
    );
  }

  const hasAnyReviews = (ratingStatsData?.total_reviews ?? 0) > 0;
  const hasNoReviews = !hasAnyReviews && isReviewsListSuccess && !isReviewsListLoading;
  // Content: ürünün en az 1 değerlendirmesi varsa göster (filtre/liste durumundan bağımsız)
  const showContent = hasAnyReviews;

  return (
    <div className="py-6 container-xs md:py-8">
      {urlSync}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 space-y-4">
          {productCardData && (
            <ReviewProductCard data={productCardData} productSlug={slug} />
          )}
          {sellerInfoData && <ReviewSellerInfo data={sellerInfoData} />}
          <FilterSection filterRowData={filtersData} />
        </div>
        <div className="lg:col-span-7 space-y-6">
          {showContent && ratingStatsData && (
            <ReviewHeader
              title={ratingStatsData.title}
              rating={ratingStatsData.average_rating}
              reviewCount={ratingStatsData.total_reviews}
              commentCount={ratingStatsData.comment_count}
            />
          )}
          {showContent && searchBarData && (
            <SearchAndSort
              placeholder={searchBarData.placeholder}
              sortOptions={searchBarData.sort_options}
            />
          )}
          {showContent && infoBoxData && (
            <SizeRecommendation
              text={infoBoxData.text}
              backgroundColor={infoBoxData.background_color}
              textColor={infoBoxData.text_color}
              icon={infoBoxData.icon}
              iconBackgroundColor={infoBoxData.icon_background_color}
            />
          )}
          {showContent && aiSummaryData && <ReviewSummary data={aiSummaryData} />}
          {showContent && <PhotoReviews />}
          <div className="space-y-4">
            {isReviewsListLoading && reviewsList.length === 0 && (
              <>
                {Array.from({ length: 3 }).map((_, i) => (
                  <ReviewCardSkeleton key={i} />
                ))}
              </>
            )}
            {hasNoReviews && (
              <EmptyState
                icon={MessageSquare}
                title="Henüz Değerlendirme Yok"
                description="Bu ürün için henüz değerlendirme yapılmamış. İlk değerlendirmeyi siz yapabilirsiniz!"
                className="my-8"
              />
            )}
            {reviewsList.length > 0 && (
              <>
                {reviewsList.map((review) => (
                  <ReviewCard key={review.id} review={review} />
                ))}
                <InfiniteScrollTrigger
                  onLoadMore={loadMoreReviews}
                  hasMore={hasMore}
                  isLoading={isReviewsListLoading}
                  threshold={0.1}
                  rootMargin="400px"
                />
                {isReviewsListLoading && reviewsList.length > 0 && (
                  <div className="w-full py-8 flex justify-center">
                    <div className="flex items-center gap-3 text-muted-foreground animate-fade-in">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-main border-t-transparent" />
                      <span className="text-sm font-medium">
                        Daha fazla değerlendirme yükleniyor...
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <FilterSidebar />
    </div>
  );
};

export default ProductReviewClient;